<template>
    <b-card class="flex-grow-1 overflow-auto bg-white mx-2 px-2">
        <!-- Formulaire de saisie du nouveau login de service -->
        <b-row class="pb-2">
            <b-col/>
            <b-col cols="auto" align-self="center">
                <!-- Barre d'actions sur l'utilisateur [annuler/sauvegarde]-->
                <b-button-toolbar key-nav :aria-label="$t('ToolbarButtons')">
                    <!--:disabled="!$store.getters['CheckAction']('Read', undefined, Action)"-->
                    <b-button @click="onSubmitCancel()" :variant="'outline-' + Variant" v-b-tooltip.noninteractive.hover :title="$t('Cancel')"
                        class="btn-circle p-0">
                        <i class="bi-ban"/>
                    </b-button>
                    
                    <!--v-if="$store.getters['CheckAction']('Write', undefined, Action) "-->
                    <b-button-group  class="mx-1">
                        <!-- $t('AddServiceLogin') / Ajouter un utilisateur -->
                        <!-- :disabled="!$store.getters['CheckAction']('Write', undefined, Action)" -->
                        <b-avatar :title="$t('Save')" :variant="Variant" button button-type="submit" :form="$id('userAddEdit')" badge-top 
                            :badge-variant="isWarnInErrorFields ? 'danger' : 'info'" badge-class="bg-transparent p-0" size="2rem"
                            :disabled="ServiceLoginForCompare !== null && !isFormChanged"
                        >
                            <template #default>
                                <i class="bi bi-floppy" />
                            </template>
                            <template #badge v-if="warnUpdatedFields() || warnInErrorFields()">
                                <!-- WarnUpdatedFields : Des champs ont été modifiés -->
                                <b-icon v-if="isWarnUpdatedFields && !isWarnInErrorFields"
                                    v-b-tooltip.noninteractive.hover :title="$t('WarnUpdatedFields')" icon="exclamation" variant="light" />

                                <!-- WarnInErrorFields : Certains champs comportent des erreurs-->
                                <b-icon v-if="isWarnInErrorFields"
                                    v-b-tooltip.noninteractive.hover :title="$t('WarnInErrorFields')" icon="exclamation" variant="light" />
                            </template>
                        </b-avatar>
                    </b-button-group>
                </b-button-toolbar>
            </b-col>
        </b-row>

        <b-row>
            <b-col>
                <!-- Validateur du formulaire (fourni méthode handleSubmit pour la validation du formulaire) -->
                <ValidationObserver ref="serviceLoginForm" v-slot="{ handleSubmit , valid }" slim>
                    <b-form :id="$id('userAddEdit')" @submit.prevent="handleSubmit(onSubmitSuccess); onSubmitInvalid(valid); " novalidate> <!-- encapsule onSubmit dans la méthode de validation -->
                        <b-row>
                            <!-- Colonne formulaire -->
                            <b-col cols="9">
                                <!-- Section Identité -->
                                <b-row>
                                    <b-col>
                                        <b-card>
                                            <b-form-group
                                                    :label="$t('Identity')"
                                                    label-cols="12"
                                                    label-cols-lg="12"
                                                    label-class="font-weight-bold text-dark pt-0 pb-2"
                                                    label-align-sm="left"
                                                    label-size="lg" />

                                            <!-- Login -->
                                            <ValidationProvider v-slot="{ errors, valid, changed }" rules="required|max:256">
                                                <b-form-group id="login" label-for="login-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1" 
                                                    :invalid-feedback="errors[0]">
                                                    <template #label>
                                                        {{ $t('Login') }}<span class="font-weight-bold text-danger"> *</span>
                                                    </template>
                                                    <b-form-input id="login-input" v-model="serviceLogin.Login" type="text" :disabled="ServiceLoginForCompare !== null" trim :state="getState(valid, changed, false)"/>
                                                </b-form-group>
                                            </ValidationProvider>

                                            <!-- Mot de passe -->
                                            <ValidationProvider v-slot="{ errors, valid, changed, validated }" rules="max:256">
                                                <b-form-group id="password" label-for="password-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1"
                                                    :invalid-feedback="errors[0]">
                                                    <template #label>
                                                        {{ $t('Password') }}<span class="font-weight-bold text-danger"> *</span>
                                                    </template>
                                                    <b-form-input id="password-input" v-model="serviceLogin.Password" type="password" :required="false" trim :state="getState(valid, changed, validated)"/>
                                                </b-form-group>
                                            </ValidationProvider>

                                            <!-- Connection Cliente -->
                                            <ValidationProvider v-slot="{ errors, valid, changed, validated }" rules="required|max:256">
                                                <b-form-group id="connection" label-for="connection-input" :label-cols="sizeOfLabel" :content-cols="sizeOfInput" class="mb-1" 
                                                    :invalid-feedback="errors[0]">
                                                    <template #label>
                                                        {{ $t('Connection') }}<span class="font-weight-bold text-danger"> *</span>
                                                    </template>
                                                    <b-form-select id="connection-input" v-model="serviceLogin.ConnectionRef" plain :disabled="ServiceLoginForCompare !== null" :options="connectionsOptions" :state="getState(valid, changed, validated)"/>
                                                </b-form-group>
                                            </ValidationProvider>
                                        </b-card>
                                    </b-col>
                                </b-row>
                            </b-col>
                            <!-- Colonne "Aide contextuelle" + Remarques -->
                            <b-col cols="3">
                                <!-- Aide contextuelle -->
                                <b-row>
                                    <b-col>
                                        <b-card>
                                            <p><b-icon icon="question-circle"/><span class="pl-2 font-weight-semibold">{{ $t(('ContextualHelp')) }}</span></p>
                                            <p>{{ $t('ContextualHelp1') }}</p>
                                        </b-card>
                                    </b-col>
                                </b-row>
                            </b-col>
                        </b-row>
                    </b-form>
                </ValidationObserver>
            </b-col>
        </b-row>
    </b-card>
</template>

<script lang="ts">
/** Types vue.js */
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';

/** Variables d'environnements */
import { API_URL } from '@/App.settings';

/** Axios pour appels webAPI back-end */
import axios, { AxiosResponse } from 'axios';

/** Types personnalisés */
import Dictionary from '@/types/Dictionary';

/** Validation du formulaire */
import '@/vee-rules'
import { ValidationObserver, ValidationProvider } from 'vee-validate';

/**Fichier de traduction local */
import locales from './locales/ServiceLoginAddEdit.json'

/** Composants enfants */
import { CompareUtils } from '@/components/commons/Utils';
import { ServiceLoginInformation } from './ServiceLoginInformation';
import ListItem from '@/types/fields/ListItem';

/** Composant de saisie ou modification des informations utilisateurs */
@Component({
    components: {
        ValidationProvider,
        ValidationObserver
	},
    i18n: { messages : locales}
})
export default class ServiceLoginAddEdit extends Vue {
    /** Déclaration du type de la ref utilisé dans le template afin d'éviter un cast */
    $refs!: {
        /** Je ne trouve pas le type de la '$ref' 'ValidationObserver', je suis obligé d'utiliser 'any' par dépit. */
        serviceLoginForm: any 
    }

    /** Variant du menu */
    @Prop()
    private Variant!: string;

    /** Droit disponible pour l'utilisateur en cours */
    @Prop()
    public Action!: string;

    /** Id de l'utilisateur */
    @Prop({ default: () => -1 })
    public LoginId : number;
    private loginId : string;

    /** Liste des base clientes */
    private connectionsOptions: { value: number, text: string }[] = [];

    /** Données du formulaire */
    private serviceLogin: ServiceLoginInformation = {
        Id: "-1",
        Login: "",
        Password: "",
        ConnectionRef: "",
        ConnectionName: ""
    }

    /** Donneés d'origine du formulaire */
    @Prop()
    public ServiceLoginForCompare: ServiceLoginInformation;

    @Watch('ServiceLoginForCompare', { deep: true, immediate: true})
    private onServiceLoginForCompareChanged() {
        if (this.ServiceLoginForCompare && this.ServiceLoginForCompare.Id) {
            this.serviceLogin = CompareUtils.Clone(this.ServiceLoginForCompare);
            console.debug("onServiceLoginForCompareChanged() : serviceLogin =", this.serviceLogin);
        }
    }

    //#region CHANGE TRACKING
    /** Indique si le formulaire a été modifié */
    private isFormChanged = false;
    
    /** Watcher de détection de changement sur le formulaire */
    @Watch('serviceLogin', { deep: true, immediate: true})
    private onServiceLoginFormChanged(){
        if(!CompareUtils.EqualsParams(this.ServiceLoginForCompare, this.serviceLogin, []))
            this.isFormChanged = true;
    }
    //#endregion CHANGE TRACKING

    //#region SIZING DU FORMULAIRE
    // Propriétés pour éviter les copier/coller redondant en cas d'ajustement
    /** Taille des labels */
    private sizeOfLabel : number = 4;
    /** Taille des inputs */
    private sizeOfInput : number = 8;
    //#endregion SIZING DU FORMULAIRE


    /**
     * Calcule l'état de validation de l'input à partir 
     * des informations de validation.
     * @param valid Indicateur de validation de l'input remonté par vee-validate 
     * @param changed Indicateur permettant de savoir si le champs est en cour d'utilisation
     */
    private getState( valid: boolean , changed: boolean, validated: boolean){
        return changed ? valid : null;
    }

    /**
     * Methode exécutée lors de la soumission du formulaire après validation
     * Si l'on arrive ici, c'est que le formulaire est validé. 👍
     */
    private async onSubmitSuccess() {
        console.debug("ServiceLoginAddEdit - onSubmitSuccess()");

        const usrId = this.LoginId;

        /** Netoyage des champ de saisie vitaux */
        this.serviceLogin.Login = this.serviceLogin.Login.trim();

        /** Pousse le nouvel utilisateur */
        if (await this.save(this.serviceLogin)) {
            this.$emit('SaveEvent');
        }
    }

    /**
     * Methode exécuté lors de l'annulation de la soumission du formulaire
     * Si l'on arrive ici, c'est que le formulaire est annulé.
     */
    private async onSubmitCancel(){
        /** Indique l'annulation pour le composant parent. */
        this.$emit('CancelEvent');
    }
    
    /** Méthode exécute lors de l'échec de la soumission du formulaire. Affiche qu'une erreur est présente dans la saisie. */
    private async onSubmitInvalid(valid:boolean){
        if(!valid){
             /** Check si le champ email est vide */
            this.$bvToast.toast(this.$t('ErrorForm').toString(), { 
                title: this.$t('Error').toString(),
                autoHideDelay: 10000,
                variant: "danger",
                solid: true,
                appendToast: true,
                toaster: "b-toaster-top-center"
            });
        }
    }

    /** 
     * Enregistre le login de service sur le serveur
     * @param serviceLogin Login de service à pousser sur le serveur 
     */
    private async save(serviceLogin: ServiceLoginInformation): Promise<boolean>{
        console.debug("ServiceLoginAddEdit - save()");

        await this.$store.dispatch("ShowLoadingMask", this.$t('LoadingSave'));

        let result = false;

        /** Arguments */
        const args: Dictionary<any> = {
            Token: Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "",
            Data: serviceLogin
        };

        /** Pousse l'utilisateur */
        try{
            const response: AxiosResponse<ServiceLoginInformation,any> = await axios.post<ServiceLoginInformation>(API_URL + "Supervision/ServiceLogins/Save", args)
            if(response) {
                /** Affichage du succes */
                this.$bvToast.toast(this.$t('ServiceLoginSaved').toString(), { // "Une erreur s'est produite."
                    title: this.$t('Success').toString(),
                    autoHideDelay: 10000,
                    variant: "success",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
                /** Enregistrement du LoginId de l'utilisateur créé. */
                this.loginId = response.data.Id;
                result = true;
            }
        }catch(err){
            if (err.response && err.response.status === 409) {
                // Conflit sur le login
                this.$bvToast.toast(this.$t('LoginConflict').toString(), { // "Une erreur s'est produite."
                    title: this.$t('Error').toString(),
                    autoHideDelay: 10000,
                    variant: "danger",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            }else{
                console.log(err);
                this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // "Une erreur s'est produite."
                    title: this.$t('Error').toString(),
                    autoHideDelay: 10000,
                    variant: "danger",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            }
        }finally{
            await this.$store.dispatch("HideLoadingMask", {});
        }
        return result;
    }

    /** Indique si un champ a été mis à jour */
    private isWarnUpdatedFields: boolean = false;

    /** Indique si un champ a été mis à jour */
    private warnUpdatedFields(): boolean {
        this.isWarnUpdatedFields =  this.isFormChanged ;
        return this.isWarnUpdatedFields;
    }


    /** Indique si un champ est en erreur */
    private isWarnInErrorFields: boolean = false;

    /** Indique si un champ est en erreur */
    private warnInErrorFields(): boolean {
        this.isWarnInErrorFields = this.$refs.serviceLoginForm && this.$refs.serviceLoginForm.errors && this.$refs.serviceLoginForm.flags['validated'] && this.$refs.serviceLoginForm.flags['invalid'] && Object.keys(this.$refs.serviceLoginForm.errors).length > 0
        return this.isWarnInErrorFields;
    }

    /** Initialisation du composant */
    async created() {
        /** Récupération des données utilisateur... */
        this.$store.dispatch("ShowLoadingMask", this.$t('LoadingServiceLoginInformation'));   
        
        this.loginId = this.LoginId.toString();

        /** Récupère la liste des bases clientes disponibles */
        // Connexion/GetBaseList
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

        const response: AxiosResponse<ListItem[],any> = await axios.post<ListItem[]>(API_URL + "Connexion/GetBaseList", args)
        if(response.data) {
            /** Retrait du premier élément "SUPERVISION" qui n'est pas un client*/
            this.connectionsOptions = response.data.slice(1).map(p => ({
                value: p.Id,
                text: p.Label
            }));
        }

        if(this.LoginId > -1){
            /** Recupération des informations de l'utilisateurs. Réactualisé depuis le serveur */
            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
            args["LoginId"] = this.LoginId;

            const response: AxiosResponse<ServiceLoginInformation,any> = await axios.post<ServiceLoginInformation>(API_URL + "Supervision/ServiceLogins/GetServiceLogin", args)
            
            if(response.data) {
                Object.assign(this.serviceLogin, response.data);
                // this.serviceLogin = response.data;
                this.ServiceLoginForCompare = CompareUtils.Clone(response.data);
            }
        }

        /** Chargement long */
        this.$store.dispatch("HideLoadingMask", {}); 
    }
}
</script>

<style scoped>

:deep(input[type=radio]:checked) {
        /** Change la couleur du bouton radio */
        accent-color: var(--Variant) !important;
    }
</style>